import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import { INTAKE_COLORS, INTAKE_FONT } from "../../theme/intakeTokens";

function ValueBlock({ label, value, documentLabel }) {
  return (
    <Box
      sx={{
        border: `1px solid ${INTAKE_COLORS.g2}`,
        borderRadius: 2,
        p: 2,
        bgcolor: INTAKE_COLORS.surface,
      }}
    >
      <Typography sx={{ fontSize: 12, fontWeight: 700, color: INTAKE_COLORS.g4, mb: 0.5, fontFamily: INTAKE_FONT }}>
        {label}
        {documentLabel ? ` (${documentLabel})` : ""}
      </Typography>
      <Typography
        sx={{
          fontSize: 14,
          color: INTAKE_COLORS.black,
          whiteSpace: "pre-wrap",
          wordBreak: "break-word",
          fontFamily: INTAKE_FONT,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
}

export default function IntakeExtractionConflictDialog({
  open,
  conflict,
  conflictIndex = 0,
  conflictTotal = 1,
  resolving = false,
  onKeepCurrent,
  onUseNew,
  onClose,
}) {
  if (!conflict) return null;

  const sectionLabel = conflict.section_name
    ? `${conflict.section_name} → ${conflict.field_label}`
    : conflict.field_label;

  const handleClose = () => {
    if (resolving) return;
    onClose?.();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown={!onClose}
      PaperProps={{ sx: { borderRadius: 3, p: 1, maxWidth: 560, fontFamily: INTAKE_FONT } }}
    >
      <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, pb: 1, fontFamily: INTAKE_FONT }}>
        <WarningAmberIcon sx={{ color: INTAKE_COLORS.amber || "#ed6c02" }} />
        <Box>
          <Typography component="span" sx={{ fontSize: 18, fontWeight: 800, color: INTAKE_COLORS.black, fontFamily: INTAKE_FONT }}>
            Conflict detected
          </Typography>
          {conflictTotal > 1 && (
            <Typography sx={{ fontSize: 12, color: INTAKE_COLORS.g4, mt: 0.25, fontFamily: INTAKE_FONT }}>
              {conflictIndex + 1} of {conflictTotal}
            </Typography>
          )}
        </Box>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ fontSize: 13, color: INTAKE_COLORS.g5, mb: 2, fontFamily: INTAKE_FONT }}>
          Field:
          {" "}
          <Box component="span" sx={{ fontWeight: 700, color: INTAKE_COLORS.black }}>
            {sectionLabel}
          </Box>
        </Typography>
        <Box sx={{ display: "grid", gap: 1.5 }}>
          <ValueBlock
            label="Current value"
            documentLabel={conflict.current_source_document}
            value={conflict.current_value}
          />
          <ValueBlock
            label="New value"
            documentLabel={conflict.new_source_document}
            value={conflict.new_value}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5, gap: 1, flexWrap: "wrap" }}>
        <Button
          onClick={handleClose}
          disabled={resolving}
          sx={{ textTransform: "none", fontWeight: 600, color: INTAKE_COLORS.g4, fontFamily: INTAKE_FONT, mr: "auto" }}
        >
          Close
        </Button>
        <Button
          onClick={onKeepCurrent}
          disabled={resolving}
          sx={{ textTransform: "none", fontWeight: 600, color: INTAKE_COLORS.g5, fontFamily: INTAKE_FONT }}
        >
          Keep current
        </Button>
        <Button
          variant="contained"
          onClick={onUseNew}
          disabled={resolving}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            px: 3,
            bgcolor: INTAKE_COLORS.blue,
            "&:hover": { bgcolor: INTAKE_COLORS.blueDark },
            fontFamily: INTAKE_FONT,
          }}
        >
          Use new value
        </Button>
      </DialogActions>
    </Dialog>
  );
}
